import { Button } from "./ui/button";
import {
  ShieldAlert,
  Link2Off,
  Sparkles,
  Clock,
  Mail,
  KeyRound,
  Globe,
  ArrowLeft,
} from "lucide-react";

const TIPS = [
  {
    icon: Sparkles,
    title: "Too good to be true? It is.",
    body: "Free $100 cards, mystery prizes, and unexpected refunds are classic scam bait. Real brands don't hand out money for filling a form.",
  },
  {
    icon: Clock,
    title: "Watch for fake urgency.",
    body: "Countdown timers, “expires in 10 minutes,” and “only 3 left” are pressure tactics designed to bypass your judgment. Slow down.",
  },
  {
    icon: Globe,
    title: "Inspect the URL — every time.",
    body: "Hover or long-press the link before tapping. Look for odd domains (.cfd, .top, misspellings of real brands, extra hyphens, lookalike characters).",
  },
  {
    icon: Link2Off,
    title: "Unexpected redirects = red flag.",
    body: "If a “claim” page bounces you to a totally unrelated domain, close the tab. Legitimate offers stay on the brand's own site.",
  },
  {
    icon: Mail,
    title: "Verify out-of-band.",
    body: "Got a “reward” email or text? Don't click. Open the brand's official app or type their URL directly into your browser to check.",
  },
  {
    icon: KeyRound,
    title: "Never enter sensitive info casually.",
    body: "Email, phone, ZIP, SSN, card numbers, OTPs — only on sites you trust and reached yourself. When in doubt, don't.",
  },
];

export default function RevealScreen({ onReset }) {
  return (
    <main
      data-testid="reveal-screen"
      className="min-h-screen bg-[#0b1220] text-slate-100"
    >
      <div className="max-w-2xl mx-auto px-5 pt-12 pb-20">
        {/* Header */}
        <div className="rise-in">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 text-[11px] font-semibold tracking-widest mb-6">
            <ShieldAlert className="w-3.5 h-3.5" /> TRAINING SIMULATION
          </div>

          <h1
            data-testid="reveal-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5"
          >
            You just got
            <br />
            <span className="text-emerald-400">phished.</span>
          </h1>

          <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-3 max-w-xl">
            Don't worry — this was a <span className="text-emerald-300 font-semibold">simulation</span>. Nothing was sent, nothing was stored, and no reward existed.
          </p>
          <p className="text-slate-400 text-sm sm:text-base leading-relaxed max-w-xl">
            On a real attack page, the data you typed would now be in the hands of a scammer. Here's how to spot the next one before it gets you.
          </p>
        </div>

        <div className="accent-rule my-10" />

        {/* Tips */}
        <div className="mb-12">
          <p className="text-xs font-bold tracking-[0.2em] text-emerald-400 mb-5">
            6 WAYS TO SPOT A REAL PHISHING ATTEMPT
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <article
                  key={i}
                  data-testid={`tip-card-${i}`}
                  className="rise-in bg-slate-900/60 border border-slate-800 rounded-2xl p-5 hover:border-emerald-400/40 hover:bg-slate-900 transition-colors"
                  style={{ animationDelay: `${i * 90}ms` }}
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-emerald-400" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2 text-slate-100">
                    {tip.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{tip.body}</p>
                </article>
              );
            })}
          </div>
        </div>

        {/* Recap */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-6 mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-amber-300 mb-3">
            WHAT THIS PAGE FAKED
          </p>
          <ul className="space-y-2 text-sm text-slate-300">
            <li className="flex gap-2"><span className="text-emerald-400">✓</span> Eye-catching reward and brand-style visual</li>
            <li className="flex gap-2"><span className="text-emerald-400">✓</span> Countdown timer and “limited offer” urgency</li>
            <li className="flex gap-2"><span className="text-emerald-400">✓</span> Fake testimonials to build false trust</li>
            <li className="flex gap-2"><span className="text-emerald-400">✓</span> A form requesting unnecessary personal data</li>
            <li className="flex gap-2"><span className="text-emerald-400">✓</span> A “verifying…” loader to feel official</li>
          </ul>
        </div>

        <Button
          onClick={onReset}
          data-testid="try-again-button"
          variant="outline"
          className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white hover:border-emerald-400/50 h-12 px-6 rounded-xl font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Run the simulation again
        </Button>

        <p className="mt-12 text-center text-[11px] text-slate-500">
          Phishing-awareness training demo · No data collected, no PII stored
        </p>
      </div>
    </main>
  );
}
